/**
 * Which template the prototype on screen belongs to, kept current as its files change (US 359).
 *
 * One answer for everything that depends on it - the device the preview is framed in, and the
 * vendor bundle the light preview loads - so the two cannot disagree. They used to: the frame
 * came from a picker and the bundle from `package.json`, which the store never holds, so a
 * mobile prototype could sit in a phone frame running the web bundle.
 *
 * The phone's OS is read here from the shell's `src/App.tsx`. The published page cannot see the
 * files and takes it from a message the prototype's `index.html` posts; Chef writes both from the
 * same choice, and both files are seeded, so they agree unless one is edited by hand.
 *
 * Reactive on purpose. A new prototype opens with no files and turns mobile only when the build
 * writes the seeded Framework7 shell, so a value read once when the workspace opened would stay
 * `web` for the whole build.
 */

import { useStore } from '@nanostores/react';
import { useMemo } from 'react';
import { workbenchStore } from '../stores/workbench';
import { templateIdForFiles, type TemplateId } from './headlessTemplate';

export function usePrototypeTemplate(): TemplateId {
  const files = useStore(workbenchStore.files);
  return useMemo(() => {
    const plain: Record<string, string> = {};
    for (const [path, entry] of Object.entries(files)) {
      if (entry && typeof entry.content === 'string') plain[path] = entry.content;
    }
    return templateIdForFiles(plain);
  }, [files]);
}

/** The phone OS a mobile prototype is built for. */
export type MobileOs = 'ios' | 'android';

/** The seeded shell, where Chef writes Framework7's theme (`mobileShell.ts`). */
const MOBILE_APP_PATHS = ['src/App.tsx', '/home/project/src/App.tsx'];

/** The opening `<App …>` tag of a source, past any `>` inside a prop; comments do not count. */
function appOpeningTag(source: string): string | undefined {
  const code = source.replace(/\/\*[\s\S]*?\*\/|(^|[^:])\/\/[^\n]*/g, '$1');
  const open = /<App(?=[\s/>])/.exec(code);
  if (!open) return undefined;
  let depth = 0;
  let quote = '';
  for (let i = open.index; i < code.length; i++) {
    const ch = code[i];
    if (quote) {
      if (ch === quote) quote = '';
    } else if (ch === '"' || ch === "'" || ch === '`') {
      quote = ch;
    } else if (ch === '{') {
      depth++;
    } else if (ch === '}') {
      depth--;
    } else if (ch === '>' && depth === 0) {
      return code.slice(open.index, i + 1);
    }
  }
  return undefined;
}

/**
 * The OS of a mobile prototype, read from its seeded shell: Chef writes `theme="ios"` or
 * `theme="md"` into `src/App.tsx` from the founder's choice in the design system (#683).
 * Anything else, including a prototype built before the choice existed, is Android.
 */
export function mobileOsForFiles(files: Record<string, string>): MobileOs {
  const path = MOBILE_APP_PATHS.find((known) => typeof files[known] === 'string');
  const tag = path ? appOpeningTag(files[path]) : undefined;
  // `theme="ios"`, `theme={'ios'}` or `theme = "ios"`; `data-theme` is another prop.
  return tag && /\stheme\s*=\s*\{?\s*["'`]ios["'`]/.test(tag) ? 'ios' : 'android';
}

/** The OS of the prototype on screen, kept current as its files change. */
export function usePrototypeMobileOs(): MobileOs {
  const files = useStore(workbenchStore.files);
  return useMemo(() => {
    const plain: Record<string, string> = {};
    for (const [path, entry] of Object.entries(files)) {
      if (MOBILE_APP_PATHS.includes(path) && entry && typeof entry.content === 'string') {
        plain[path] = entry.content;
      }
    }
    return mobileOsForFiles(plain);
  }, [files]);
}

/**
 * The device a prototype of this template is previewed in. A mobile prototype is framed on the
 * phone of the OS it was built for: an iPhone for iOS, a Pixel for Android (Material).
 */
export function deviceIdForTemplate(template: TemplateId, os: MobileOs = 'android'): string {
  if (template !== 'mobile') return 'desktop';
  return os === 'ios' ? 'iphone-17' : 'pixel-9';
}
